import assert from 'assert';
import {interfaces} from 'inversify';
import {getType} from 'typesafe-actions';
import {assign, Machine, sendParent} from 'xstate';
import {container} from '../../ioc/container';
import {services} from '../../ioc/services';
import {Player} from '../../services/player/player';
import {
  requestNextAction,
  requestPauseAction,
  requestPlayAction,
  requestPreviousAction,
  requestProgressUpdateAction,
} from './playerActions';
import {trackChangedAction} from './playerMachineActions';
import {PlayerMachineContext} from './PlayerMachineContext';

const getPlayer = (context: PlayerMachineContext): Player => {
  const player = context.player;
  if (!player) {
    throw new Error('context.player is empty');
  }
  return player;
};
export const playerMachine = Machine<PlayerMachineContext>(
  {
    strict: true,
    initial: 'player',
    id: 'playerMachineC',
    states: {
      player: {
        onEntry: 'createPlayer',
        invoke: {src: 'runPlayer'},
        initial: 'init',
        on: {
          TRACK_CHANGED: {
            actions: ['updateMachineState', sendParent(({track}) => trackChangedAction({track}))],
          },
          TIMEUPDATE: {
            actions: ['updateMachineState'],
          },
          PAUSE: {
            target: '.paused',
            actions: ['updateMachineState'],
          },
          PLAY: {target: '.playing', actions: ['updateMachineState']},
          [getType(requestNextAction)]: {
            actions: 'requestNext',
          },
          [getType(requestPreviousAction)]: {
            actions: 'requestPrevious',
          },
          [getType(requestProgressUpdateAction)]: {
            actions: 'updateProgress',
          },
        },
        states: {
          init: {},
          playing: {
            on: {
              [getType(requestPauseAction)]: {
                actions: (context) => getPlayer(context).pause(),
              },
            },
          },
          paused: {
            id: 'paused',
            on: {
              [getType(requestPlayAction)]: {
                actions: (context) => getPlayer(context).play(),
              },
            },
          },
        },
      },
    },
  },
  {
    services: {
      runPlayer: (context) => {
        const player: Player = getPlayer(context);

        return (callback) => {
          player.on('init', ({track, paused}) => {
            callback({type: 'TRACK_CHANGED'});
            const string = paused ? 'PAUSE' : 'PLAY';
            callback(string);
          });
          player.on('timeupdate', ({currentTime}) => {
            callback({type: 'TIMEUPDATE', payload: currentTime});
          });
          player.on('playing', (args) => {
            callback({type: 'PLAY', payload: args});
          });
          player.on('pause', (args) => {
            callback({type: 'PAUSE', payload: args});
          });

          player.on('track-change', (args) => {
            callback({type: 'TRACK_CHANGED'});
          });

          void player.init();
        };
      },
    },
    actions: {
      updateMachineState: assign({
        currentTime: (context, action) => action.payload,
        progress: (context) => getPlayer(context).progress,
        track: (context) => getPlayer(context).currentTrack,
        duration: (context) => getPlayer(context).duration,
      }),
      createPlayer: assign({
        player: ({album, track, player}) => {
          if (player) {
            return player;
          }
          assert(album, 'Album is not provided to playerMachine');
          const Player = container.get<interfaces.Newable<Player>>(services.Player);
          return new Player(album, track);
        },
      }),
      requestNext: (context) => getPlayer(context).next(),
      requestPrevious: (context) => getPlayer(context).previous(),
      updateProgress: (context, action) => {
        const progress = action.payload;
        const player = getPlayer(context);
        player.updateCurrentTime(player.duration * progress);
      },
    },
  },
);
